import { infiniteQueryOptions, type InfiniteData, type QueryClient } from '@tanstack/react-query';
import { api, unwrap } from '@/api/client';
import type { BulkIn, ItemOut, ItemPatch, PageOf, ReviewStatus } from '@/api/research';
import { qk } from '@/api/queries';

export { researchRunsQuery, snapshotsQuery } from '@/api/queries';

export const PAGE_SIZE = 100;
export type ItemFilters = { review_status?: ReviewStatus; search?: string };

/** Products of the report, paged by the server (100 a page) and loaded as the table scrolls. */
export const productsQuery = (orgId: string, f: ItemFilters) =>
  infiniteQueryOptions({
    queryKey: qk.researchItems(orgId, { kind: 'product', ...f }),
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET('/admin/organizations/{org_id}/research-items', {
          params: { path: { org_id: orgId }, query: { kind: 'product', page: pageParam, size: PAGE_SIZE, ...f } },
        }),
      ),
    initialPageParam: 1,
    getNextPageParam: (last: PageOf<ItemOut>) => (last.page < last.pages ? last.page + 1 : undefined),
  });

export const versionsQuery = (itemId: string) => ({
  queryKey: ['research', 'versions', itemId],
  queryFn: () => unwrap(api.GET('/admin/research-items/{item_id}/versions', { params: { path: { item_id: itemId } } })),
});

export function patchItem(id: string, body: ItemPatch) {
  return unwrap(api.PATCH('/admin/research-items/{item_id}', { params: { path: { item_id: id } }, body }));
}

export function bulk(body: BulkIn) {
  return unwrap(api.POST('/admin/research-items/bulk', { body }));
}

export function merge(ids: string[], keep: string) {
  return unwrap(api.POST('/admin/research-items/merge', { body: { ids, keep } }));
}

export function approveSnapshot(orgId: string) {
  return unwrap(api.POST('/admin/organizations/{org_id}/research-snapshots', { params: { path: { org_id: orgId } }, body: {} }));
}

export function startRun(orgId: string) {
  return unwrap(api.POST('/admin/organizations/{org_id}/research-runs', { params: { path: { org_id: orgId } } }));
}

export function cancelRun(runId: string) {
  return unwrap(api.POST('/admin/research-runs/{run_id}/cancel', { params: { path: { run_id: runId } } }));
}

/** Applies a change to every cached products page of the organization (optimistic, then refetched). */
export function updateCachedItems(qc: QueryClient, orgId: string, fn: (items: ItemOut[]) => ItemOut[]) {
  qc.setQueriesData<InfiniteData<PageOf<ItemOut>>>({ queryKey: ['research', 'items', orgId] }, (data) =>
    data ? { ...data, pages: data.pages.map((p) => ({ ...p, items: fn(p.items) })) } : data,
  );
}
